import React, { useEffect, useMemo } from 'react';
import { ChoseOnboardingType, OnboardingContainer, UploadMediaCard } from '../../../components';
import {
  CardsContainer,
  Content,
  DescriptionContainer,
  DescriptionText,
  NoteText,
  StyledButton,
} from './UploadMediaPage.styles';
import { Text20Bold } from '../../../components/utils/Text/Text.styles';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser, setUserStatus } from '../../../store/slices/user/slice';
import { ReactComponent as ImageIcon } from '../../../assets/icons/image-04.svg';
import { ReactComponent as Image1Icon } from '../../../assets/icons/image-03.svg';
import { ReactComponent as FilmIcon } from '../../../assets/icons/film-01.svg';
import { ReactComponent as Film1Icon } from '../../../assets/icons/film-03.svg';
import { ReactComponent as FolderIcon } from '../../../assets/icons/folder.svg';
import {
  INFLUENCER_REQUEST_ONBOARDING_TYPE,
  MIN_NUM_OF_FREE_PHOTOS,
  MIN_NUM_OF_FREE_VIDEOS,
  MIN_NUM_OF_PREMIUM_PHOTOS,
  MIN_NUM_OF_PREMIUM_VIDEOS,
} from '../../../utils/constants';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  UPLOAD_BUNDLES,
  UPLOAD_FREE_ASSISTED_PHOTOS,
  UPLOAD_FREE_ASSISTED_VIDEOS,
  UPLOAD_FREE_PHOTOS,
  UPLOAD_FREE_VIDEOS,
  UPLOAD_MEDIA,
  UPLOAD_PAID_ASSISTED_PHOTOS,
  UPLOAD_PAID_ASSISTED_VIDEOS,
  UPLOAD_PAID_PHOTOS,
  UPLOAD_PAID_VIDEOS,
} from '../../../components/App/routes';
import { getAlbumMediaTags, saveGallery } from '../../../store/slices/influencerRequest/asyncThunks';
import { selectInfluencerRequestActionsPending } from '../../../store/slices/influencerRequest/slice';
import { notifyError } from '../../../utils/notify';

const UploadMediaPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { saveGallery: saveGalleryPending } = useSelector(selectInfluencerRequestActionsPending);

  let { pathname } = useLocation();

  useEffect(() => {
    dispatch(getAlbumMediaTags());
  }, [dispatch]);

  const CARDS = [
    {
      title: 'Free Photos',
      icon: <ImageIcon color="#1672BA" />,
      description: `Upload at least ${MIN_NUM_OF_FREE_PHOTOS} photos fans can see at no cost.`,
      uploadedMediaLength: user.numOfFreePhotos,
      minUploadMediaLength: MIN_NUM_OF_FREE_PHOTOS,
      buttonTitle: 'Upload Free Photos',
      onButtonClick: () => {
        navigate(
          `${
            user.onboardingType === INFLUENCER_REQUEST_ONBOARDING_TYPE.SELF
              ? UPLOAD_FREE_PHOTOS
              : UPLOAD_FREE_ASSISTED_PHOTOS
          }`,
        );
      },
    },
    {
      title: 'Paid Photos',
      icon: <Image1Icon />,
      description: `Upload at least ${MIN_NUM_OF_PREMIUM_PHOTOS} photos fans can unlock for a fee.`,
      uploadedMediaLength: user.numOfPremiumPhotos,
      minUploadMediaLength: MIN_NUM_OF_PREMIUM_PHOTOS,
      buttonTitle: 'Upload Paid Photos',
      onButtonClick: () => {
        navigate(
          `${
            user.onboardingType === INFLUENCER_REQUEST_ONBOARDING_TYPE.SELF
              ? UPLOAD_PAID_PHOTOS
              : UPLOAD_PAID_ASSISTED_PHOTOS
          }`,
        );
      },
    },
    {
      title: 'Free Videos',
      icon: <FilmIcon />,
      description: `Upload at least ${MIN_NUM_OF_FREE_VIDEOS} videos fans can see at no cost.`,
      uploadedMediaLength: user.numOfFreeVideos,
      minUploadMediaLength: MIN_NUM_OF_FREE_VIDEOS,
      buttonTitle: 'Upload Free Videos',
      onButtonClick: () => {
        navigate(
          `${
            user.onboardingType === INFLUENCER_REQUEST_ONBOARDING_TYPE.SELF
              ? UPLOAD_FREE_VIDEOS
              : UPLOAD_FREE_ASSISTED_VIDEOS
          }`,
        );
      },
    },
    {
      title: 'Paid Videos',
      icon: <Film1Icon />,
      description: `Upload at least ${MIN_NUM_OF_PREMIUM_VIDEOS} videos fans can unlock for a fee.`,
      uploadedMediaLength: user.numOfPremiumVideos,
      minUploadMediaLength: MIN_NUM_OF_PREMIUM_VIDEOS,
      buttonTitle: 'Upload Paid Videos',
      onButtonClick: () => {
        navigate(
          `${
            user.onboardingType === INFLUENCER_REQUEST_ONBOARDING_TYPE.SELF
              ? UPLOAD_PAID_VIDEOS
              : UPLOAD_PAID_ASSISTED_VIDEOS
          }`,
        );
      },
    },
    ...(user.onboardingType === INFLUENCER_REQUEST_ONBOARDING_TYPE.SELF
      ? [
          {
            title: 'Bundle',
            icon: <FolderIcon />,
            description: 'Combine related photos and/or videos into bundle.',
            buttonTitle: 'Create Bundles',
            onButtonClick: () => {
              navigate(`${UPLOAD_BUNDLES}`);
            },
          },
        ]
      : []),
  ];

  const btnDisabled = useMemo(
    () =>
      user.numOfPremiumVideos < MIN_NUM_OF_PREMIUM_VIDEOS ||
      user.numOfFreeVideos < MIN_NUM_OF_FREE_VIDEOS ||
      user.numOfPremiumPhotos < MIN_NUM_OF_PREMIUM_PHOTOS ||
      user.numOfFreePhotos < MIN_NUM_OF_FREE_PHOTOS,
    [user],
  );

  const onSubmit = () => {
    dispatch(saveGallery())
      .unwrap()
      .then(({ userStatus }) => {
        dispatch(setUserStatus(userStatus));
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  return (
    <OnboardingContainer>
      {!user.onboardingType ? (
        <ChoseOnboardingType />
      ) : (
        <Content>
          {pathname === `/${UPLOAD_MEDIA}` ? (
            <>
              {/* <HeaderContainer>
                <Text20Bold>Gallery</Text20Bold>
              </HeaderContainer> */}
              <DescriptionContainer>
                <Text20Bold>Upload Photos & Videos</Text20Bold>
                <DescriptionText>Use the sections below to add your media.</DescriptionText>
              </DescriptionContainer>
              <CardsContainer>
                {CARDS.map((card, index) => {
                  return (
                    <UploadMediaCard
                      key={index}
                      title={card.title}
                      icon={card.icon}
                      description={card.description}
                      uploadedMediaLength={card.uploadedMediaLength}
                      buttonTitle={card.buttonTitle}
                      onButtonClick={card.onButtonClick}
                      minUploadMediaLength={card.minUploadMediaLength}
                    />
                  );
                })}
              </CardsContainer>
              <NoteText>Please upload all the required media to continue</NoteText>
              <StyledButton title="Continue" isLoading={saveGalleryPending} disabled={btnDisabled} onClick={onSubmit} />
            </>
          ) : (
            <Outlet />
          )}
        </Content>
      )}
    </OnboardingContainer>
  );
};

export default UploadMediaPage;
